import React, { useEffect, useState, useRef } from "react";
import { Button, TextField, Autocomplete } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { fetchCategoryEvents } from "../../Apiservices/Auth/servers/fetchCategoryEvents";
import * as Yup from "yup";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { locale, addLocale } from "primereact/api";
import config from "../../config";
import { createEvent } from "../../Apiservices/Auth/servers/createEvents";
import { useSelector, useDispatch } from "react-redux";
import CustomCalendar from "./Customcalender";
import { ReactComponent as EVENT_CALENDAR } from "../assets/event-calendar.svg";
import './ThurentEventForm.css'
import priest from "../../assets/images/quickservices/priest.png";
import tent from "../../assets/images/quickservices/tent.png";
import catering from "../../assets/images/quickservices/catering.png";
import { setServiceType, openServiceDialog } from '../../store/serviceBookingSlice';
import ServiceBookingDialog from "./quickservices/ServiceBookingDialog";
import ProviderSelectionDialog from "./quickservices/ProviderSelectionDialog";
import { Dialog } from "primereact/dialog";
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import useWindowSize from "../../util/useWindowSize";

const ThurentEventForm = ({
  onCloseDailog,
  refreshEvents,
  preSelectedEvent = null,
  openUpdateEventDialog
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const { width } = useWindowSize();
  const [error, setError] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [eventData, setEventData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  
  // Location states
  const [location, setLocation] = useState({ lat: 0, lng: 0 });
  const [address, setAddress] = useState("");
  const [area, setArea] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  
  const mapRef = useRef(null);

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey,
    libraries: ["places", "maps"],
  });

  // Initialize autocomplete services
  useEffect(() => {
    if (isLoaded && window.google) {
      setAutocompleteService(new window.google.maps.places.AutocompleteService());
      setPlacesService(new window.google.maps.places.PlacesService(document.createElement('div')));
    }
  }, [isLoaded]);

  const eventLocalStorageKey = config.eventLocalStorageKey;
  const today = new Date();
  const oneYearFromToday = new Date(
    new Date().setFullYear(today.getFullYear() + 1)
  );

  addLocale("in", {
    now: "OK",
  });

  // Fetch address from coordinates
  const fetchAddress = (lat, lng) => {
    if (isLoaded) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          const result = results[0];
          const addressComponents = result.address_components;
          const getComponent = (type) =>
            addressComponents.find((comp) => comp.types.includes(type))
              ?.long_name || "";

          setAddress(result.formatted_address);
          setArea(getComponent("sublocality") || getComponent("locality"));
          setCity(getComponent("locality"));
          setState(getComponent("administrative_area_level_1"));
          setCountry(getComponent("country"));
          
          // Update formik values
          formik.setFieldValue("address", result.formatted_address);
          formik.setFieldValue("area", getComponent("sublocality") || getComponent("locality"));
          formik.setFieldValue("city", getComponent("locality"));
          formik.setFieldValue("state", getComponent("administrative_area_level_1"));
          formik.setFieldValue("country", getComponent("country"));
          formik.setFieldValue("latitude", lat);
          formik.setFieldValue("longitude", lng);
        } else {
          console.error("Geocoder failed due to:", status);
        }
      });
    }
  };

  // Get current location
  useEffect(() => {
    if (isLoaded && (!location.lat || !location.lng)) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setLocation({ lat: latitude, lng: longitude });
            fetchAddress(latitude, longitude);
          },
          (error) => console.error("Error fetching location:", error)
        );
      }
    }
  }, [isLoaded]);

  // Handle location input change with autocomplete
  const handleLocationInputChange = (event, value) => {
    setAddress(value);
    formik.setFieldValue("address", value);
    
    if (value?.length > 2 && autocompleteService) {
      autocompleteService.getPlacePredictions(
        {
          input: value,
          types: ['establishment', 'geocode'],
          componentRestrictions: { country: 'in' }
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setLocationSuggestions(predictions.map(prediction => ({
              description: prediction.description,
              placeId: prediction.place_id
            })));
          } else {
            setLocationSuggestions([]);
          }
        }
      );
    } else {
      setLocationSuggestions([]);
    }
  };

  // Handle location selection from autocomplete
  const handleLocationSelect = (event, value) => {
    if (value && placesService) {
      const request = {
        placeId: value.placeId,
        fields: ['geometry', 'formatted_address', 'address_components', 'name']
      };

      placesService.getDetails(request, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          setLocation({ lat, lng });
          setAddress(place.formatted_address || value.description);
          formik.setFieldValue("address", place.formatted_address || value.description);
          formik.setFieldValue("latitude", lat);
          formik.setFieldValue("longitude", lng);
          
          // Update address components
          if (place.address_components) {
            const getComponent = (type) =>
              place.address_components.find((comp) => comp.types.includes(type))
                ?.long_name || "";

            setArea(getComponent("sublocality") || getComponent("locality"));
            setCity(getComponent("locality"));
            setState(getComponent("administrative_area_level_1"));
            setCountry(getComponent("country"));
            
            formik.setFieldValue("area", getComponent("sublocality") || getComponent("locality"));
            formik.setFieldValue("city", getComponent("locality"));
            formik.setFieldValue("state", getComponent("administrative_area_level_1"));
            formik.setFieldValue("country", getComponent("country"));
          }

          // Center map on selected location
          if (mapRef.current) {
            mapRef.current.panTo({ lat, lng });
            mapRef.current.setZoom(15);
          }
        }
      });
    }
    setLocationSuggestions([]);
  };

  // Handle map click
  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setLocation({ lat, lng });
    fetchAddress(lat, lng);
  };

  // Handle marker drag end
  const handleMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setLocation({ lat, lng });
    fetchAddress(lat, lng);
  };

  // Use current location
  const useCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ lat: latitude, lng: longitude });
          fetchAddress(latitude, longitude);
        },
        (error) => {
          console.error("Error fetching location:", error);
          alert("Unable to fetch location. Please enable location services.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = "";
        const [events_data] = await Promise.all([fetchCategoryEvents(token)]);
        setEventData(events_data);
      } catch (error) {
        setError("Failed to fetch data.");
      }
    };
    fetchData();
    let existingData;
    if (existingData) {
      const parsedData = JSON.parse(existingData);
      if (parsedData.Date) {
        parsedData.Date = new Date(parsedData.Date);
      }
      formik.setValues(parsedData);
    }
    locale("in");

    if (preSelectedEvent) {
      handleEventInputChange(null, preSelectedEvent?.name);
    }
  }, []);

  const handleServiceButtonClick = (serviceType) => {
    console.log("Selected service type:", serviceType);
    dispatch(setServiceType(serviceType));
    dispatch(openServiceDialog());
  };

  const handleEventInputChange = (event, value) => {
    handleEventChange("event", value);
    if (value.length >= 0) {
      const newFilteredOptions = eventData
        .map((e) => e.name)
        .filter((option) => option.toLowerCase().includes(value.toLowerCase()));
      setFilteredOptions(newFilteredOptions);
    } else {
      setFilteredOptions([]);
    }
  };

  const alertdialog = () => {
    confirmDialog({
      message: "Please Login to Create Event",
      header: "Login",
      defaultFocus: "accept",
      accept: () => {
        const existingData = localStorage.getItem(eventLocalStorageKey);
        if (existingData) {
          const parsedData = JSON.parse(existingData);
          const updatedValues = { ...parsedData, isPending: true };
          localStorage.setItem(
            eventLocalStorageKey,
            JSON.stringify(updatedValues)
          );
        }
        navigate("/login");
      },
    });
  };

  const formik = useFormik({
    initialValues: {
      Date: "",
      eventTitle: "",
      eventDesc: "",
      address: "",
      area: "",
      city: "",
      state: "",
      country: "",
      latitude: null,
      longitude: null,
      context: { eventData },
      event: { event: "", id: "" },
    },
    validationSchema: Yup.object({
      event: Yup.object({
        event: Yup.string().required("Event is required")
      }),
      Date: Yup.string().required("Date is required"),
      address: Yup.string().required("Location is required"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        console.log('Form submitted:', values);
        if (isLoggedIn) {
          const eventBody = {
            eventTitle: values.eventTitle,
            event: values.event,
            eventDesc: values.eventDesc,
            Date: values.Date && values.Date.toString(),
            Address: values.address,
            area: values.area,
            city: values.city,
            state: values.state,
            country: values.country,
            locationLat: values.latitude,
            locationLong: values.longitude,
          };
          
          try {
            const response = await createEvent(eventBody);
            console.log("Event created successfully:", response);
            
            if (refreshEvents) {
              refreshEvents();
            }
            
            formik.resetForm();
            localStorage.removeItem(eventLocalStorageKey);
            
            if (onCloseDailog) onCloseDailog();
            
          } catch (error) {
            console.log("Something Went Wrong while Creating Event ", error);
            setError(error.message || "Failed to create event");
          }
        } else {
          alertdialog();
        }
      } catch (ex) {
        setError(ex.toString());
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const updateLocalStorage = (updatedValues) => {
    if (!isLoggedIn) {
      localStorage.setItem(eventLocalStorageKey, JSON.stringify(updatedValues));
    }
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    formik.setFieldValue(field, value);
    updateLocalStorage({ ...formik.values, [field]: value });
  };

  const handleEventChange = (field, value) => {
    const selectedEvent = eventData.find((e) => e.name === value);
    if (!selectedEvent) return;
    formik.setFieldValue(field, {
      event: selectedEvent.name,
      id: selectedEvent.id,
    });
    updateLocalStorage({
      ...formik.values,
      [field]: {
        event: selectedEvent.name,
        id: selectedEvent.id,
      },
    });
  };

  const calendarRef = useRef(null);
  const closeCalender = () => {
    calendarRef.current.hide();
  };
  
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  const renderAutocomplete = (label, options, field) => (
    <Autocomplete
      open={autocompleteOpen}
      onOpen={() => setAutocompleteOpen(true)}
      onClose={() => setAutocompleteOpen(false)}
      options={options}
      getOptionLabel={(option) => option}
      disabled={preSelectedEvent !== null}
      value={formik.values[field]?.event || ""}
      onInputChange={(event, value, reason) => {
        handleEventInputChange(event, value, reason);
        formik.setFieldValue(field, {
          event: value,
          id: "",
        });
        if (reason === "input") {
          setAutocompleteOpen(true);
        }
      }}
      onChange={(event, value) => {
        if (!value) return;
        handleEventChange(field, value);
        setAutocompleteOpen(false);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"
          error={
            formik.touched[field]?.event &&
            Boolean(formik.errors[field]?.event)
          }
          helperText={
            formik.touched[field]?.event &&
            formik.errors[field]?.event
          }
          onBlur={() => {
            formik.setFieldTouched(field, true);
            setTimeout(() => setAutocompleteOpen(false), 200);
          }}
          InputProps={{
            ...params.InputProps,
            sx: {
              display: "flex",
              padding: "10px 14px",
              alignItems: "center",
              gap: "8px",
              borderRadius: "8px",
              background: "#FFF",
            },
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                <ExpandMoreIcon sx={{ color: "#888" }} />
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option}>
          {option}
        </li>
      )}
    />
  );

  // Location Picker Dialog Component
  const LocationPickerDialog = () => (
    <Dialog
      header="Select Location on Map"
      visible={showLocationPicker}
      style={{ width: '90vw', maxWidth: '1200px' }}
      onHide={() => setShowLocationPicker(false)}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <Button 
            label="Cancel" 
            onClick={() => setShowLocationPicker(false)}
            sx={{ textTransform: 'none' }}
          />
          <Button 
            label="Confirm Location" 
            variant="contained"
            onClick={() => setShowLocationPicker(false)}
            sx={{ 
              textTransform: 'none',
              background: '#383838',
              '&:hover': { background: '#2a2a2a' }
            }}
          />
        </div>
      }
    >
      <div>
        {/* Location Search Autocomplete */}
        <Autocomplete
          freeSolo
          options={locationSuggestions}
          getOptionLabel={(option) => 
            typeof option === 'string' ? option : option.description
          }
          onInputChange={handleLocationInputChange}
          onChange={handleLocationSelect}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search for a location..."
              size="small"
              sx={{ marginBottom: '16px', width: '100%' }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.placeId}>
              {option.description}
            </li>
          )}
        />
        
        <div style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
          <Button
            onClick={useCurrentLocation}
            variant="outlined"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            📍 Use My Current Location
          </Button>
          <span style={{ fontSize: '12px', color: '#666', alignSelf: 'center' }}>
            Click on map to select location
          </span>
        </div>
        
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{
              height: width > 768 ? "50vh" : "40vh",
              width: "100%",
            }}
            center={location.lat ? location : { lat: 20.5937, lng: 78.9629 }}
            zoom={12}
            onClick={handleMapClick}
            onLoad={(map) => (mapRef.current = map)}
          >
            {location.lat && location.lng && (
              <MarkerF
                position={location}
                draggable
                onDragEnd={handleMarkerDragEnd}
              />
            )}
          </GoogleMap>
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '400px',
            background: '#f5f5f5'
          }}>
            Loading Map...
          </div>
        )}
        
        {address && (
          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            background: '#f5f5f5', 
            borderRadius: '8px',
            fontSize: '14px'
          }}>
            <strong>Selected Location:</strong>
            <p style={{ margin: '8px 0 0 0', color: '#555' }}>
              {address}
            </p>
            {city && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#777' }}>
              {city}, {state}
            </p>}
          </div>
        )}
      </div>
    </Dialog>
  );

  return (
    <div
      className="event-form th-plan"
      style={{
        boxShadow: "none",
      }}
    >
      {!isLoggedIn &&
        <>
          <div className="provider-services2">
            <div className="servicesinfo"
              onClick={() => handleServiceButtonClick('priest')}>
              <div className="servicetext">
                <span>Book Priest</span>
                <span>For every ritual</span>
              </div>
              <img
                src={priest}
                alt="priest"
                className="serviceImage"
              />
            </div>
            <div className="servicesinfo"
              onClick={() => handleServiceButtonClick('catering')}>
              <div className="servicetext">
                <span>Order Catering </span>
                <span>Delicious & fresh</span>
              </div>
              <img
                src={catering}
                alt="priest"
                className="serviceImage"
              />
            </div>
            <div className="servicesinfo"
              onClick={() => handleServiceButtonClick('tent')}>
              <div className="servicetext">
                <span>Rent tent houses</span>
                <span>Decor & essentials</span>
              </div>
              <img
                src={tent}
                alt="priest"
                className="serviceImage"
              />
            </div>
          </div>
          <span style={{ margin: "8px" }}>Or</span>
        </>
      }
      <span className="th-plan__text text-sm">
        Create your event, We help manage
      </span>
      <form
        onSubmit={formik.handleSubmit}
        style={{
          width: "100%",
        }}
      >
        {renderAutocomplete(
          "Select Event - Type min 3 character",
          eventData.map((e) => e.name),
          "event"
        )}

        <TextField
          name="eventDesc"
          label="Whose event is it?"
          value={formik.values.eventDesc}
          onChange={handleFieldChange("eventDesc")}
          size="small"
          className="w-full my-3"
          variant="outlined"
          InputProps={{
            sx: {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              alignSelf: "stretch",
              borderRadius: "8px",
              background: "#FFF",
              fontFamily: "Inter !important",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
            },
          }}
          InputLabelProps={{
            sx: {
              fontFamily: "Inter !important",
              color: "#888",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
            },
          }}
        />

        {/* Location Field with Map Picker Button */}
        <div style={{ position: "relative", marginBottom: "16px" }}>
          <TextField
            name="address"
            label="Event Location / Venue"
            value={formik.values.address}
            onChange={handleFieldChange("address")}
            onBlur={formik.handleBlur}
            size="small"
            fullWidth
            variant="outlined"
            error={formik.touched.address && Boolean(formik.errors.address)}
            helperText={formik.touched.address && formik.errors.address}
            InputProps={{
              sx: {
                borderRadius: "8px",
                background: "#FFF",
                fontFamily: "Inter !important",
                fontSize: "16px",
                paddingRight: "56px",
              },
            }}
          />
          <Button
            type="button"
            onClick={() => setShowLocationPicker(true)}
            sx={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              minWidth: "40px",
              height: "40px",
              padding: "8px",
              borderRadius: "8px",
              background: "#f0f0f0",
              '&:hover': {
                background: "#e0e0e0",
              }
            }}
          >
            <LocationOnIcon sx={{ color: "#383838" }} />
          </Button>
        </div>

        {/* Hidden fields for location details */}
        <input type="hidden" name="area" value={formik.values.area} />
        <input type="hidden" name="city" value={formik.values.city} />
        <input type="hidden" name="state" value={formik.values.state} />
        <input type="hidden" name="country" value={formik.values.country} />
        <input type="hidden" name="latitude" value={formik.values.latitude} />
        <input type="hidden" name="longitude" value={formik.values.longitude} />

        <div style={{ marginBottom: "10px" }}>
          <CustomCalendar
            value={formik.values.Date}
            onChange={(e) => {
              formik.setFieldValue("Date", e.value);
              updateLocalStorage({
                ...formik.values,
                Date: e.value && e.value.toString(),
              });
            }}
            minDate={today}
            maxDate={oneYearFromToday}
            showTime={true}
            dateFormat="dd-M-yy"
            placeholder="Event Date"
            showIcon={true}
            icon={<EVENT_CALENDAR />}
            inputStyle={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              alignSelf: "stretch",
              borderRadius: "8px",
              background: "#FFF",
              fontFamily: "Inter",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
              width: "100%",
              border: formik.touched.Date && formik.errors.Date ? "1px solid red" : "1px solid rgba(0, 0, 0, 0.23)",
            }}
          />
          {formik.touched.Date && formik.errors.Date && (
            <p className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeSmall MuiFormHelperText-contained">
              {formik.errors.Date}
            </p>
          )}
        </div>

        {error && <p style={{ color: "red" }}>{error}</p>}

        <Button
          variant="contained"
          type="submit"
          disabled={isSubmitting}
          className="mt-4 th-nav__provider__txt"
          sx={{
            display: "flex",
            textTransform: "initial",
            height: "48px",
            padding: "12px 24px",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            alignSelf: "stretch",
            borderRadius: "8px",
            background: "#383838",
            width: "100%",
            opacity: isSubmitting ? 0.7 : 1,
          }}
        >
          {isSubmitting ? "Creating Event..." : "Create my event"}
        </Button>
      </form>

      <LocationPickerDialog />
      <ServiceBookingDialog />
      <ProviderSelectionDialog />
      <ConfirmDialog />
    </div>
  );
};

export default ThurentEventForm;